import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Scale, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is the CIA Triad?",
        icon: "🛡️",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-100 p-6 rounded-2xl border-2 border-slate-200">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">The Foundation of Security</h3>
                    <p className="text-slate-700 mb-4">
                        The <strong>CIA Triad</strong> is the core framework used by security professionals
                        worldwide. Every security decision involves balancing these three principles.
                    </p>
                    <p className="text-slate-700">
                        Note: This CIA has nothing to do with the spy agency! It stands for
                        <strong className="text-blue-600"> Confidentiality</strong>,
                        <strong className="text-green-600"> Integrity</strong>, and
                        <strong className="text-yellow-600"> Availability</strong>.
                    </p>
                </div>
                <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200">
                    <h4 className="font-bold text-yellow-800 mb-3">🎯 The Trade-Off Triangle</h4>
                    <p className="text-yellow-700 mb-3">You can't maximize all three at once. Increasing one often decreases another:</p>
                    <ul className="text-yellow-700 space-y-2">
                        <li className="flex items-center gap-2"><X size={16} /> More Confidentiality → Less Availability</li>
                        <li className="flex items-center gap-2"><X size={16} /> More Availability → Harder to maintain Integrity</li>
                        <li className="flex items-center gap-2"><X size={16} /> Maximum security = Less convenient access</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Confidentiality",
        icon: "🔒",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                            <Lock className="text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-xl text-blue-800">Confidentiality</h3>
                            <p className="text-blue-600">"Keeping Secrets"</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        Only the right people can see the information. Like a locked diary that only you can read.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <h4 className="font-bold text-green-800 mb-2">✅ How it's done:</h4>
                        <ul className="text-green-700 text-sm space-y-1">
                            <li>• Encryption (scrambling data)</li>
                            <li>• Passwords and authentication</li>
                            <li>• Access control lists</li>
                            <li>• Physical security</li>
                        </ul>
                    </div>
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                        <h4 className="font-bold text-red-800 mb-2">❌ When it fails:</h4>
                        <ul className="text-red-700 text-sm space-y-1">
                            <li>• Data breaches</li>
                            <li>• Leaked passwords</li>
                            <li>• Social engineering</li>
                            <li>• Insider threats</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Integrity",
        icon: "✅",
        content: (
            <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                            <Shield className="text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-xl text-green-800">Integrity</h3>
                            <p className="text-green-600">"No Tampering"</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        The data is accurate and hasn't been changed. Like a sealed letter that arrives exactly as it was sent.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <h4 className="font-bold text-blue-800 mb-2">✅ How it's done:</h4>
                        <ul className="text-blue-700 text-sm space-y-1">
                            <li>• Checksums and hashes</li>
                            <li>• Digital signatures</li>
                            <li>• Version control</li>
                            <li>• Audit logs</li>
                        </ul>
                    </div>
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                        <h4 className="font-bold text-red-800 mb-2">❌ When it fails:</h4>
                        <ul className="text-red-700 text-sm space-y-1">
                            <li>• Ransomware attacks</li>
                            <li>• Database corruption</li>
                            <li>• Man-in-the-middle attacks</li>
                            <li>• Accidental overwrites</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 4,
        title: "Availability",
        icon: "👁️",
        content: (
            <div className="space-y-6">
                <div className="bg-yellow-50 p-6 rounded-2xl border-2 border-yellow-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                            <Eye className="text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-xl text-yellow-800">Availability</h3>
                            <p className="text-yellow-600">"Open for Business"</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        Systems and data are accessible when you need them. Like a light switch that always works when you flip it.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <h4 className="font-bold text-green-800 mb-2">✅ How it's done:</h4>
                        <ul className="text-green-700 text-sm space-y-1">
                            <li>• Redundant servers</li>
                            <li>• Load balancing</li>
                            <li>• Regular backups</li>
                            <li>• DDoS protection</li>
                        </ul>
                    </div>
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                        <h4 className="font-bold text-red-800 mb-2">❌ When it fails:</h4>
                        <ul className="text-red-700 text-sm space-y-1">
                            <li>• System outages</li>
                            <li>• DDoS attacks</li>
                            <li>• Hardware failures</li>
                            <li>• Natural disasters</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 5,
        title: "Real-World Examples",
        icon: "🌍",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg mb-4">Different organizations prioritize different parts of the triad:</p>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-5 rounded-xl border-2 border-blue-200">
                        <div className="text-3xl mb-3">🏦</div>
                        <h3 className="font-bold text-blue-800 mb-2">Banks</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            <strong>Priority:</strong> Integrity + Confidentiality
                        </p>
                        <p className="text-slate-500 text-xs">
                            They'll take downtime over showing wrong amounts!
                        </p>
                    </div>
                    <div className="bg-yellow-50 p-5 rounded-xl border-2 border-yellow-200">
                        <div className="text-3xl mb-3">🏥</div>
                        <h3 className="font-bold text-yellow-800 mb-2">Hospitals</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            <strong>Priority:</strong> Availability (24/7 access)
                        </p>
                        <p className="text-slate-500 text-xs">
                            Doctors need records NOW to save lives!
                        </p>
                    </div>
                    <div className="bg-red-50 p-5 rounded-xl border-2 border-red-200">
                        <div className="text-3xl mb-3">🕵️</div>
                        <h3 className="font-bold text-red-800 mb-2">Military</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            <strong>Priority:</strong> Confidentiality (secrets)
                        </p>
                        <p className="text-slate-500 text-xs">
                            Some systems are intentionally hard to access!
                        </p>
                    </div>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl border border-slate-200 mt-4">
                    <h4 className="font-bold text-slate-800 mb-2">🎮 Ready to Test Your Balance?</h4>
                    <p className="text-slate-600 text-sm">
                        In the activity, you'll allocate a "security budget" across all three pillars for different scenarios!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ SCENARIO DATA ============
interface TriadValues {
    confidentiality: number;
    integrity: number;
    availability: number;
}

const SCENARIOS = [
    {
        name: 'Hospital Records',
        description: 'Protecting patient medical data',
        optimal: { confidentiality: 90, integrity: 85, availability: 95 },
        hint: 'Patients need access 24/7, but data must be private and accurate'
    },
    {
        name: 'Trading Platform',
        description: 'Stock trading application',
        optimal: { confidentiality: 70, integrity: 95, availability: 99 },
        hint: 'Data accuracy and uptime are critical; accounts are protected but secondary'
    },
    {
        name: 'Secret Gov Agency',
        description: 'Classified information system',
        optimal: { confidentiality: 99, integrity: 90, availability: 60 },
        hint: 'Secrecy is paramount, even if access is limited'
    },
];

// ============ GAME COMPONENT ============
const CiaTriadGame = ({ onComplete }: { onComplete: () => void }) => {
    const [values, setValues] = useState<TriadValues>({
        confidentiality: 50,
        integrity: 50,
        availability: 50
    });
    const [budget] = useState(300);
    const [scenarioIndex, setScenarioIndex] = useState(0);
    const [showFeedback, setShowFeedback] = useState(false);
    const [completed, setCompleted] = useState(false);

    const scenario = SCENARIOS[scenarioIndex];
    const used = values.confidentiality + values.integrity + values.availability;
    const remaining = budget - used;

    const calculateScore = () => {
        const opt = scenario.optimal;
        const diffC = Math.abs(values.confidentiality - opt.confidentiality);
        const diffI = Math.abs(values.integrity - opt.integrity);
        const diffA = Math.abs(values.availability - opt.availability);
        const totalDiff = diffC + diffI + diffA;
        return Math.max(0, 100 - totalDiff);
    };

    const score = calculateScore();

    const handleSubmit = () => {
        setShowFeedback(true);
        if (score >= 70 && !completed) {
            setCompleted(true);
            onComplete();
        }
    };

    const nextScenario = () => {
        setScenarioIndex((scenarioIndex + 1) % SCENARIOS.length);
        setValues({ confidentiality: 50, integrity: 50, availability: 50 });
        setShowFeedback(false);
    };

    const restart = () => {
        setCompleted(false);
        setScenarioIndex(0);
        setShowFeedback(false);
    };

    if (completed) {
        return (
            <ModuleSuccess
                title="Guardian of the Triad"
                badgeId="badge_cyber-security"
                onRetry={restart}
                nextModulePath="/modules/password-strength"
            >
                <div className="bg-slate-100 rounded-2xl p-6 text-left border-2 border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-2">🛡️ Shield Secured</h4>
                    <p className="text-slate-600 mb-4 text-sm">
                        You balanced the trade-offs perfectly.
                    </p>
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 text-xs text-yellow-800 italic">
                        💡 <strong>Fun Fact:</strong> Most military systems prioritize <strong>Confidentiality</strong> (secrets), while Amazon prioritizes <strong>Availability</strong> (uptime).
                    </div>
                </div>
            </ModuleSuccess>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className="text-3xl font-black text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Scale className="text-red-500" /> CIA Triad Balancer
                </h2>
                <p className="text-slate-600">
                    Balance the three pillars for each scenario. You have a limited "security budget"!
                </p>
            </div>

            {/* Scenario Card */}
            <div className="bg-slate-100 rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">{scenario.name}</h3>
                        <p className="text-slate-600">{scenario.description}</p>
                    </div>
                    <button
                        onClick={nextScenario}
                        className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-xl text-sm font-bold transition-colors"
                    >
                        Next Scenario →
                    </button>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 flex items-center gap-3">
                    <AlertTriangle className="text-yellow-600 flex-shrink-0" />
                    <p className="text-yellow-700 text-sm">{scenario.hint}</p>
                </div>
            </div>

            {/* Budget Indicator */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200 flex items-center justify-between">
                <span className="font-bold text-slate-600">Security Budget</span>
                <div className="flex items-center gap-4">
                    <div className="w-48 h-3 bg-slate-200 rounded-full overflow-hidden">
                        <motion.div
                            className={`h-full ${remaining >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            animate={{ width: `${Math.min(100, (used / budget) * 100)}%` }}
                        />
                    </div>
                    <span className={`font-mono font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {remaining >= 0 ? remaining : 'OVER'} pts left
                    </span>
                </div>
            </div>

            {/* Triad Sliders */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Confidentiality */}
                <div className="bg-blue-50 rounded-2xl p-6 border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                            <Lock className="text-white" size={20} />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-800">Confidentiality</h4>
                            <p className="text-xs text-slate-500">Who can see the data?</p>
                        </div>
                    </div>
                    <div className="text-4xl font-black text-blue-600 text-center mb-4">
                        {values.confidentiality}%
                    </div>
                    <input
                        type="range"
                        min="0" max="100"
                        value={values.confidentiality}
                        onChange={e => setValues({ ...values, confidentiality: Number(e.target.value) })}
                        className="w-full accent-blue-500"
                    />
                </div>

                {/* Integrity */}
                <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                            <CheckCircle className="text-white" size={20} />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-800">Integrity</h4>
                            <p className="text-xs text-slate-500">Is the data accurate?</p>
                        </div>
                    </div>
                    <div className="text-4xl font-black text-green-600 text-center mb-4">
                        {values.integrity}%
                    </div>
                    <input
                        type="range"
                        min="0" max="100"
                        value={values.integrity}
                        onChange={e => setValues({ ...values, integrity: Number(e.target.value) })}
                        className="w-full accent-green-500"
                    />
                </div>

                {/* Availability */}
                <div className="bg-yellow-50 rounded-2xl p-6 border-2 border-yellow-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                            <Eye className="text-white" size={20} />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-800">Availability</h4>
                            <p className="text-xs text-slate-500">Can you access it?</p>
                        </div>
                    </div>
                    <div className="text-4xl font-black text-yellow-600 text-center mb-4">
                        {values.availability}%
                    </div>
                    <input
                        type="range"
                        min="0" max="100"
                        value={values.availability}
                        onChange={e => setValues({ ...values, availability: Number(e.target.value) })}
                        className="w-full accent-yellow-500"
                    />
                </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
                <button
                    onClick={handleSubmit}
                    disabled={remaining < 0}
                    className={`px-12 py-4 rounded-2xl font-bold text-lg transition-all ${remaining < 0
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-500 text-white shadow-lg'
                        }`}
                >
                    Submit Configuration
                </button>
            </div>

            {/* Feedback */}
            {showFeedback && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-2xl p-6 border-2 ${score >= 70
                        ? 'bg-green-50 border-green-300'
                        : 'bg-red-50 border-red-300'
                        }`}
                >
                    <h3 className={`text-xl font-bold mb-2 ${score >= 70 ? 'text-green-700' : 'text-red-700'}`}>
                        {score >= 70 ? '✅ Great Balance!' : '❌ Not Quite Right'}
                    </h3>
                    <p className="text-slate-600 mb-4">
                        Your score: <span className="font-bold text-slate-800">{score}%</span>
                    </p>
                    <div className="text-sm text-slate-500">
                        <strong>Optimal for {scenario.name}:</strong><br />
                        Confidentiality: {scenario.optimal.confidentiality}% |
                        Integrity: {scenario.optimal.integrity}% |
                        Availability: {scenario.optimal.availability}%
                    </div>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const CiaTriad = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('cia-triad', 100, 'badge_cyber-security');
    };

    return (
        <LessonWrapper
            moduleName="CIA Triad"
            moduleEmoji="🛡️"
            moduleColor="red"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <CiaTriadGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default CiaTriad;
